import React, { useState, useEffect } from "react";
import { FaUsers, FaShoppingCart, FaBox, FaDollarSign, FaChartLine, FaEye, FaArrowUp, FaArrowDown } from "react-icons/fa";
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import api from "../../utilis/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const Overview = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalUsers: 0,
      totalOrders: 0,
      totalProducts: 0,
      totalRevenue: 0,
    },
    monthlyRevenue: [],
    orderStatusStats: [],
    recentOrders: [],
    topProducts: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.get("/admin/dashboard/stats");
      
      if (response.data?.success) {
        setDashboardData(response.data.data);
      } else {
        throw new Error("Failed to fetch dashboard data");
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
      
      // Fallback to mock data for development
      setDashboardData({
        stats: {
          totalUsers: 156,
          totalOrders: 89,
          totalProducts: 45,
          totalRevenue: 12450.75,
        },
        monthlyRevenue: [
          { _id: { year: 2024, month: 1 }, revenue: 2500, orders: 15 },
          { _id: { year: 2024, month: 2 }, revenue: 3200, orders: 22 },
          { _id: { year: 2024, month: 3 }, revenue: 2800, orders: 18 },
          { _id: { year: 2024, month: 4 }, revenue: 4100, orders: 28 },
        ],
        orderStatusStats: [
          { _id: "Processing", count: 12 },
          { _id: "Shipped", count: 8 },
          { _id: "Delivered", count: 65 },
          { _id: "Cancelled", count: 4 },
        ],
        recentOrders: [],
        topProducts: []
      });
    } finally {
      setLoading(false);
    }
  };

  // Chart configurations
  const revenueChartData = {
    labels: dashboardData.monthlyRevenue.map(item => {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return monthNames[item._id.month - 1];
    }),
    datasets: [
      {
        label: 'Revenue ($)',
        data: dashboardData.monthlyRevenue.map(item => item.revenue),
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const orderStatusChartData = {
    labels: dashboardData.orderStatusStats.map(item => item._id),
    datasets: [
      {
        data: dashboardData.orderStatusStats.map(item => item.count),
        backgroundColor: [
          '#3b82f6', // Processing - Blue
          '#f59e0b', // Shipped - Amber
          '#10b981', // Delivered - Green
          '#ef4444', // Cancelled - Red
        ],
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: '#9ca3af',
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#9ca3af',
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
      y: {
        ticks: {
          color: '#9ca3af',
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
  };

  const StatCard = ({ title, value, icon: Icon, color, trend, trendValue }) => (
    <div className="bg-[#121218] p-6 rounded-xl border border-white/10 hover:bg-[#1a1a1f] transition-all duration-300 group">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-gray-400 text-sm font-medium mb-1">{title}</p>
          <p className="text-2xl font-bold text-white mb-2">
            {loading ? (
              <div className="animate-pulse bg-gray-600 h-8 w-20 rounded"></div>
            ) : (
              value
            )}
          </p>
          {trend && (
            <div className={`flex items-center text-sm ${
              trend === 'up' ? 'text-green-400' : 'text-red-400'
            }`}>
              {trend === 'up' ? <FaArrowUp className="mr-1" /> : <FaArrowDown className="mr-1" />}
              {trendValue}% from last month
            </div>
          )}
        </div>
        <div className={`p-4 rounded-xl ${color} group-hover:scale-110 transition-transform duration-300`}>
          <Icon className="text-white text-2xl" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-8 min-h-screen bg-[#0a0a0f]">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-3 bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">
          Dashboard Overview
        </h1>
        <p className="text-gray-400 text-lg">Welcome back! Here's what's happening with your store today.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={dashboardData.stats.totalUsers.toLocaleString()}
          icon={FaUsers}
          color="bg-gradient-to-br from-blue-500 to-blue-600"
          trend="up"
          trendValue="12"
        />
        <StatCard
          title="Total Orders"
          value={dashboardData.stats.totalOrders.toLocaleString()}
          icon={FaShoppingCart}
          color="bg-gradient-to-br from-green-500 to-green-600"
          trend="up"
          trendValue="8"
        />
        <StatCard
          title="Total Products"
          value={dashboardData.stats.totalProducts.toLocaleString()}
          icon={FaBox}
          color="bg-gradient-to-br from-purple-500 to-purple-600"
          trend="up"
          trendValue="5"
        />
        <StatCard
          title="Total Revenue"
          value={`$${dashboardData.stats.totalRevenue.toLocaleString()}`}
          icon={FaDollarSign}
          color="bg-gradient-to-br from-amber-500 to-amber-600"
          trend="up"
          trendValue="15"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <div className="lg:col-span-2 bg-[#121218] rounded-xl border border-white/10 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white flex items-center">
              <FaChartLine className="mr-2 text-amber-500" />
              Revenue Trend
            </h2>
            <div className="text-sm text-gray-400">
              Last 6 months
            </div>
          </div>
          <div className="h-64">
            {loading ? (
              <div className="animate-pulse bg-gray-700 h-full rounded"></div>
            ) : (
              <Line data={revenueChartData} options={chartOptions} />
            )}
          </div>
        </div>

        {/* Order Status Chart */}
        <div className="bg-[#121218] rounded-xl border border-white/10 p-6">
          <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
            <FaShoppingCart className="mr-2 text-green-500" />
            Order Status
          </h2>
          <div className="h-64">
            {loading ? (
              <div className="animate-pulse bg-gray-700 h-full rounded-full"></div>
            ) : (
              <Doughnut 
                data={orderStatusChartData} 
                options={{
                  ...chartOptions,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        color: '#9ca3af',
                        padding: 20,
                      },
                    },
                  },
                }} 
              />
            )}
          </div>
        </div>
      </div>

      {/* Recent Orders & Top Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="bg-[#121218] rounded-xl border border-white/10 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">Recent Orders</h2>
            <button 
              onClick={() => navigate('/dashboard/orders')}
              className="text-amber-500 hover:text-amber-400 text-sm font-medium flex items-center"
            >
              <FaEye className="mr-1" /> View All
            </button>
          </div>
          <div className="space-y-3">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-700 h-16 rounded-lg"></div>
                </div>
              ))
            ) : dashboardData.recentOrders.length > 0 ? (
              dashboardData.recentOrders.slice(0, 5).map((order) => (
                <div key={order._id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                  <div>
                    <p className="text-white font-medium">{order.orderId || `#${order.orderNo}`}</p>
                    <p className="text-gray-400 text-sm">{order.user?.name || order.shippingAddress?.name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-medium">${order.totalPrice}</p>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      order.orderStatus === 'Delivered' ? 'bg-green-500/20 text-green-400' :
                      order.orderStatus === 'Shipped' ? 'bg-blue-500/20 text-blue-400' :
                      order.orderStatus === 'Processing' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}>
                      {order.orderStatus}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-400">
                No recent orders found
              </div>
            )}
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-[#121218] rounded-xl border border-white/10 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">Top Products</h2>
            <button 
              onClick={() => navigate('/dashboard/products')}
              className="text-amber-500 hover:text-amber-400 text-sm font-medium flex items-center"
            >
              <FaEye className="mr-1" /> View All
            </button>
          </div>
          <div className="space-y-3">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-700 h-16 rounded-lg"></div>
                </div>
              ))
            ) : dashboardData.topProducts.length > 0 ? (
              dashboardData.topProducts.map((item, index) => (
                <div key={item._id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center text-white font-bold mr-3">
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-white font-medium">{item.product?.name}</p>
                      <p className="text-gray-400 text-sm">{item.totalSold} sold</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-medium">${item.revenue.toFixed(2)}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-400">
                No product data available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 p-6 rounded-xl text-center hover:from-amber-500/20 hover:to-orange-500/20 transition-all duration-300">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center mx-auto mb-4">
            <FaBox className="text-white text-xl" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">Add New Product</h3>
          <p className="text-gray-400 text-sm mb-4">Create and manage your product catalog</p>
          <button 
            onClick={() => navigate('/dashboard/create-products')}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-6 py-2 rounded-lg transition-all duration-300 font-medium"
          >
            Add Product
          </button>
        </div>

        <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 p-6 rounded-xl text-center hover:from-blue-500/20 hover:to-purple-500/20 transition-all duration-300">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mx-auto mb-4">
            <FaUsers className="text-white text-xl" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">Manage Users</h3>
          <p className="text-gray-400 text-sm mb-4">View and manage user accounts</p>
          <button 
            onClick={() => navigate('/dashboard/users')}
            className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white px-6 py-2 rounded-lg transition-all duration-300 font-medium"
          >
            View Users
          </button>
        </div>

        <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 p-6 rounded-xl text-center hover:from-green-500/20 hover:to-emerald-500/20 transition-all duration-300">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mx-auto mb-4">
            <FaShoppingCart className="text-white text-xl" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">View Orders</h3>
          <p className="text-gray-400 text-sm mb-4">Monitor and process customer orders</p>
          <button 
            onClick={() => navigate('/dashboard/orders')}
            className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-6 py-2 rounded-lg transition-all duration-300 font-medium"
          >
            View Orders
          </button>
        </div>
      </div>
    </div>
  );
};

export default Overview;
